import { ElementRef } from "@angular/core";

/**
 * Utility class for instantiating AngularJS components from modern Angular components.
 * This provides a bridge between AngularJS and modern Angular applications.
 */
export class AngularJSComponentUtil {
    private activeComponents: Map<string, any> = new Map();

    /**
     * Converts camelCase string to kebab-case for HTML attributes
     * @param str - The camelCase string to convert
     * @returns The kebab-case string
     */
    private camelToKebab(str: string): string {
        return str.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();
    }

    /**
     * Creates and instantiates an AngularJS component within a modern Angular component.
     *
     * @param componentName - The name of the AngularJS component to instantiate
     * @param containerElement - The Angular ElementRef where the component should be rendered
     * @param bindings - The bindings/inputs to pass to the AngularJS component
     * @param scope - Optional parent scope for the AngularJS component
     * @returns Promise that resolves when the component is initialized
     */
    public async createAngularJSComponent(
        componentName: string,
        containerElement: ElementRef,
        bindings: any = {},
        scope?: any,
        options?: { hostClass?: string; attributeLiterals?: Record<string, string> },
    ): Promise<any> {
        return new Promise((resolve, reject) => {
            try {
                // Ensure angBridge is available
                if (!window.angBridge) {
                    throw new Error("angBridge is not available. Make sure AngularJS bridge is initialized.");
                }

                const { $compile, $rootScope, $q, element } = window.angBridge;
                const angularGlobal = window.angular as { noop?: () => void } | undefined;

                // Create a new scope for the component
                const componentScope = scope || $rootScope.$new();

                // Build component element with bindings as attributes
                let componentHtml = `<${componentName}`;
                Object.keys(bindings).forEach((key) => {
                    const value = bindings[key];
                    const kebabKey = this.camelToKebab(key);

                    // Skip null/undefined so optional `@` flags are omitted (not set to the key name).
                    if (value === null || value === undefined) {
                        return;
                    }

                    if (typeof value === "string") {
                        // `@` bindings take the attribute as a literal / interpolation — NOT a `$parse`
                        // expression. Use `{{key}}` so the scope value is interpolated (e.g. mode="edit").
                        // Inlining the raw string breaks when it contains spaces/quotes; using the bare
                        // key name (mode="mode") makes `@` literally equal "mode".
                        componentScope[key] = value;
                        componentHtml += ` ${kebabKey}="{{${key}}}"`;
                    } else if (typeof value === "function") {
                        // `&` bindings expect an invocable parent expression (e.g. `onSave()`), not a bare
                        // identifier that only returns a function reference when the child calls it.
                        const invokeName = `__s25Invoke_${key}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
                        componentScope[invokeName] = value;
                        componentHtml += ` ${kebabKey}="${invokeName}()"`;
                    } else if (typeof value === "object") {
                        // `<` / `=` bindings: attribute is a scope expression
                        componentScope[key] = value;
                        componentHtml += ` ${kebabKey}="${key}"`;
                    } else {
                        // numbers / booleans — safe to inline for `@` or `<`
                        componentHtml += ` ${kebabKey}="${value}"`;
                    }
                });
                if (options?.attributeLiterals) {
                    for (const [attributeName, literalValue] of Object.entries(options.attributeLiterals)) {
                        const escaped = String(literalValue).replace(/&/g, "&amp;").replace(/"/g, "&quot;");
                        componentHtml += ` ${attributeName}="${escaped}"`;
                    }
                }
                if (options?.hostClass) {
                    componentHtml += ` class="${options.hostClass}"`;
                }
                componentHtml += `></${componentName}>`;

                // Create the component element
                const componentElement = element(componentHtml);

                // Compile the component
                const compiledElement = $compile(componentElement)(componentScope);

                // Append to container
                element(containerElement.nativeElement).append(compiledElement);

                // Trigger digest only when one is not already running (e.g. ModalService
                // may assign data during an AngJS $digest — $digest() would throw inprog)
                if (!componentScope.$root.$$phase) {
                    componentScope.$digest();
                }
                const noop = angularGlobal?.noop ?? (() => undefined);
                $rootScope.$evalAsync(noop);

                // Nested widgets finish async setup (`$q.all` → render) after this tick;
                // a deferred digest picks up their DOM updates.
                const injector = window.angBridge.$injector as { get?: (name: string) => unknown } | undefined;
                const injectorGet = injector?.get?.bind(injector);
                if (injectorGet) {
                    const timeoutFn = injectorGet("$timeout") as (fn: () => void, delay?: number) => void;
                    const rootScopeAgain = injectorGet("$rootScope") as {
                        $digest: () => void;
                        $$phase?: string;
                    };
                    timeoutFn(() => {
                        if (!rootScopeAgain.$$phase) {
                            rootScopeAgain.$digest();
                        }
                    }, 0);
                }

                // Store reference for cleanup
                const componentId = this.generateComponentId();
                this.activeComponents.set(componentId, {
                    scope: componentScope,
                    element: compiledElement,
                    componentName,
                });

                // Wait for component initialization
                $q.when().then(() => {
                    resolve({
                        componentId,
                        scope: componentScope,
                        element: compiledElement,
                        destroy: () => this.destroyComponent(componentId),
                    });
                });
            } catch (error) {
                console.error(`Failed to create AngularJS component ${componentName}:`, error);
                reject(error);
            }
        });
    }

    /**
     * Destroys an AngularJS component and cleans up its resources.
     *
     * @param componentId - The ID of the component to destroy
     */
    public destroyComponent(componentId: string): void {
        const component = this.activeComponents.get(componentId);
        if (component) {
            try {
                // Destroy the scope
                component.scope.$destroy();

                // Remove the element
                component.element.remove();

                // Remove from active components
                this.activeComponents.delete(componentId);
            } catch (error) {
                console.error(`Error destroying AngularJS component ${component.componentName}:`, error);
            }
        }
    }

    /**
     * Destroys all active AngularJS components.
     */
    public destroyAllComponents(): void {
        this.activeComponents.forEach((component, componentId) => {
            this.destroyComponent(componentId);
        });
    }

    /**
     * Generates a unique component ID.
     */
    private generateComponentId(): string {
        return `angjs_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    /**
     * Creates a deferred promise for AngularJS components that need to resolve after initialization.
     *
     * @param componentName - The name of the component
     * @param containerElement - The container element
     * @param bindings - The bindings object
     * @param deferProperty - The property name on bindings that contains the defer promise
     * @returns Promise that resolves when the component's defer is resolved
     */
    public async createAngularJSComponentWithDefer(
        componentName: string,
        containerElement: ElementRef,
        bindings: any,
        deferProperty: string = "defer",
    ): Promise<any> {
        const { $q } = window.angBridge;

        // Create a defer for this component
        const componentDefer = $q.defer();

        // Add the defer to bindings
        const bindingsWithDefer = {
            ...bindings,
            [deferProperty]: componentDefer,
        };

        // Create the component
        const componentInstance = await this.createAngularJSComponent(
            componentName,
            containerElement,
            bindingsWithDefer,
        );

        // Return a promise that resolves when the component's defer resolves
        return componentDefer.promise.then((result: any) => {
            return {
                ...componentInstance,
                result,
            };
        });
    }
}
